import { ArrowRight, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  useCreateExternalNoteMutation,
  useDraftNoteContentMutation,
} from '@/services/api/externalNoteService';
import type {
  CreateExternalNotePayload,
  ExternalNoteAdditionalFieldConfig,
  ExternalNoteAdditionalFieldValue,
  ExternalNoteResolutionSchema,
} from '@/services/types/external-note-types';

import { DynamicAdditionalField } from './DynamicAdditionalField';

type ContentState = 'raw' | 'drafting' | 'draft_ready' | 'editing';

interface PushNoteToCmsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  rawContent: string;
  triggerType: 'staff_note' | 'task_complete' | 'standalone';
  cmsResolution: ExternalNoteResolutionSchema;
  taskTitle?: string;
  matterName?: string;
  clientName?: string;
  sourceType?: string | null;
  sourceId?: string | null;
  showSkip?: boolean;
  onSkip?: () => void;
  onSuccess?: () => void;
}

const CONTENT_STATE_BADGE: Record<
  ContentState,
  { label: string; className: string } | null
> = {
  raw: {
    label: 'UNPROCESSED',
    className: 'bg-amber-100 text-amber-700',
  },
  drafting: {
    label: 'AI DRAFTING',
    className: 'bg-violet-100 text-violet-700',
  },
  draft_ready: {
    label: 'AI DRAFT READY',
    className: 'bg-green-100 text-green-700',
  },
  editing: null,
};

function initializeFieldValues(
  configs: ExternalNoteAdditionalFieldConfig[],
): Record<string, ExternalNoteAdditionalFieldValue> {
  return Object.fromEntries(
    configs.map((config) => [
      config.external_key,
      {
        external_key: config.external_key,
        field_type: config.field_type,
        value: config.default_value ?? null,
      },
    ]),
  );
}

export const PushNoteToCmsModal: React.FC<PushNoteToCmsModalProps> = ({
  open,
  onOpenChange,
  matterId,
  rawContent,
  triggerType,
  cmsResolution,
  taskTitle,
  matterName,
  clientName,
  sourceType,
  sourceId,
  showSkip = false,
  onSkip,
  onSuccess,
}) => {
  const [content, setContent] = useState(rawContent);
  const [subject, setSubject] = useState('');
  const [contentState, setContentState] = useState<ContentState>('raw');
  const [fieldValues, setFieldValues] = useState<
    Record<string, ExternalNoteAdditionalFieldValue>
  >({});

  const [draftContent] = useDraftNoteContentMutation();
  const [createNote, { isLoading: isSubmitting }] =
    useCreateExternalNoteMutation();

  const additionalFields = useMemo(
    () => cmsResolution.config?.additional_fields ?? [],
    [cmsResolution.config],
  );

  // Initialize field values from config defaults when modal opens
  useEffect(() => {
    if (open) {
      setContent(rawContent);
      setSubject('');
      setContentState('raw');
      setFieldValues(initializeFieldValues(additionalFields));
    }
  }, [open, rawContent, additionalFields]);

  const handleFieldChange = useCallback(
    (
      externalKey: string,
      fieldType: ExternalNoteAdditionalFieldConfig['field_type'],
      value: string | number | boolean | string[] | null,
    ) => {
      setFieldValues((prev) => ({
        ...prev,
        [externalKey]: {
          external_key: externalKey,
          field_type: fieldType,
          value,
        },
      }));
    },
    [],
  );

  // Find the subject field config to avoid rendering it twice
  const subjectFieldKey = useMemo(() => {
    const subjectField = additionalFields.find(
      (f) =>
        f.external_key === 'subject' ||
        f.external_key === 'name' ||
        f.label.toLowerCase() === 'subject' ||
        f.label.toLowerCase() === 'name',
    );
    return subjectField?.external_key ?? null;
  }, [additionalFields]);

  const subjectFieldConfig = useMemo(
    () =>
      additionalFields.find((field) => field.external_key === subjectFieldKey) ??
      null,
    [additionalFields, subjectFieldKey],
  );

  // Separate subject field from other additional fields
  const nonSubjectFields = useMemo(
    () =>
      additionalFields.filter((f) => f.external_key !== subjectFieldKey),
    [additionalFields, subjectFieldKey],
  );

  const subjectValue = subjectFieldKey
    ? ((fieldValues[subjectFieldKey]?.value as string | null) ?? '')
    : subject;

  // Auto-fire AI draft when modal opens
  useEffect(() => {
    if (!open || !cmsResolution.cms_type) return;

    setContentState('drafting');
    draftContent({
      matterId,
      body: {
        raw_context: rawContent,
        trigger_type: triggerType,
        task_title: taskTitle,
        matter_name: matterName,
        client_name: clientName,
        cms_type: cmsResolution.cms_type,
      },
    })
      .unwrap()
      .then((result) => {
        setContent(result.cleaned_content);
        if (subjectFieldKey) {
          handleFieldChange(
            subjectFieldKey,
            subjectFieldConfig?.field_type ?? 'TEXT',
            result.suggested_subject,
          );
        } else {
          setSubject(result.suggested_subject);
        }
        setContentState('draft_ready');
      })
      .catch(() => {
        // On AI draft failure, keep raw content and let user edit manually
        setContentState('raw');
      });
  }, [
    clientName,
    cmsResolution.cms_type,
    draftContent,
    handleFieldChange,
    matterId,
    matterName,
    open,
    rawContent,
    subjectFieldConfig?.field_type,
    subjectFieldKey,
    taskTitle,
    triggerType,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!cmsResolution.cms_type || !content.trim()) return;

    // Validate required fields
    const missingField = additionalFields.find((field) => {
      if (!field.required) return false;
      const val = fieldValues[field.external_key]?.value;
      return val == null || val === '';
    });
    if (missingField) {
      toast.error(`${missingField.label} is required`);
      return;
    }

    try {
      await createNote({
        matterId,
        cmsType: cmsResolution.cms_type,
        body: {
          action_type: 'CREATE',
          content: content.trim(),
          additional_fields: Object.values(fieldValues),
          source_type: sourceType as CreateExternalNotePayload['source_type'],
          source_id: sourceId,
        },
      }).unwrap();

      toast.success('Note pushed to CMS');
      onSuccess?.();
      onOpenChange(false);
    } catch {
      toast.error('Failed to push note to CMS. Please try again.');
    }
  }, [
    cmsResolution.cms_type,
    content,
    additionalFields,
    fieldValues,
    createNote,
    matterId,
    sourceType,
    sourceId,
    onSuccess,
    onOpenChange,
  ]);

  const badge = CONTENT_STATE_BADGE[contentState];

  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} size="xl">
      <div className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50">
          <ArrowRight className="h-3.5 w-3.5 text-zinc-600" />
        </span>
        <DialogTitle>Push Note to CMS</DialogTitle>
      </div>
      <DialogDescription>
        {triggerType === 'task_complete' && taskTitle
          ? `Task completed \u00B7 ${taskTitle}`
          : 'Also push to the case in your CMS?'}
      </DialogDescription>

      <DialogBody className="space-y-5">
        {/* Subject */}
        <div className="space-y-2">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor="push-cms-subject"
            className="text-xs font-medium uppercase tracking-wide text-zinc-500"
          >
            Subject
          </label>
          <Input
            id="push-cms-subject"
            value={subjectValue}
            onChange={(e) => {
              if (subjectFieldKey) {
                handleFieldChange(
                  subjectFieldKey,
                  subjectFieldConfig?.field_type ?? 'TEXT',
                  e.target.value,
                );
              } else {
                setSubject(e.target.value);
              }
            }}
            placeholder="Subject line..."
          />
        </div>

        {/* Dynamic Additional Fields — side-by-side when 2+, stacked when 1 */}
        {nonSubjectFields.length > 0 && (
          <div
            className={
              nonSubjectFields.length >= 2
                ? 'grid grid-cols-2 gap-4'
                : 'space-y-5'
            }
          >
            {nonSubjectFields.map((fieldConfig) => (
              <DynamicAdditionalField
                key={fieldConfig.external_key}
                config={fieldConfig}
                value={fieldValues[fieldConfig.external_key]?.value ?? null}
                onChange={(val) =>
                  handleFieldChange(
                    fieldConfig.external_key,
                    fieldConfig.field_type,
                    val,
                  )
                }
              />
            ))}
          </div>
        )}

        {/* Note Content */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                Note Content
              </span>
              {badge && (
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${badge.className}`}
                >
                  {badge.label}
                </span>
              )}
            </div>
            <div className="text-xs text-zinc-400">
              {contentState === 'drafting' && (
                <span className="flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Cleaning up...
                </span>
              )}
              {contentState === 'draft_ready' && (
                <button
                  type="button"
                  className="font-medium text-violet-600 hover:text-violet-700"
                  onClick={() => setContentState('editing')}
                >
                  Edit
                </button>
              )}
            </div>
          </div>
          <Textarea
            id="push-cms-content"
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              if (contentState === 'draft_ready') {
                setContentState('editing');
              }
            }}
            rows={4}
            className="resize-none bg-zinc-50/50"
            readOnly={contentState === 'drafting'}
          />
        </div>
      </DialogBody>

      <DialogActions>
        {showSkip && (
          <button
            type="button"
            className="mr-auto text-sm text-zinc-500 hover:text-zinc-700"
            onClick={() => {
              onSkip?.();
              onOpenChange(false);
            }}
          >
            Skip
          </button>
        )}
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          className="gap-1.5 bg-violet-600 hover:bg-violet-700"
          onClick={handleSubmit}
          disabled={isSubmitting || !content.trim()}
        >
          {isSubmitting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ArrowRight className="h-3.5 w-3.5" />
          )}
          Push to{' '}
          {cmsResolution.cms_system ?? cmsResolution.cms_type ?? 'CMS'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
